import * as sdk from '../sdk';
import * as Sentry from '@sentry/react';
import _isEmpty from 'lodash/isEmpty';
import { updateMessageThreads, updateMessageConversations } from '../utils/storedLocalData';
import { getBrowserDetails, isUUID, tabletWidth } from '../utils/helpers';
import { getFileSizeDelay, toaster, tsCustomFormat, tsNow } from '../common';
import {
  deletePhoto,
  endImageUploadLoader,
  endPendingUpload,
  pendingUpload,
  refreshAccount,
  stopPendingPhotoUpload,
  uploadErrorFlow,
} from '../actions/profileActions';
import _uniqBy from 'lodash/uniqBy';
import _sortBy from 'lodash/sortBy';
import { Location, NavigateFunction } from 'react-router-dom';
import { CancelTokenSource } from 'axios';
import { IThread } from './types/mailActions';
import { AppDispatch, RootState } from '../store';
import { THREADS_ORDER } from '../constants/threads';
import MasterConfig from '../config/Master';
import { displayFailMessage } from './notificationActions';
import moment from 'moment';

// Helper function to check if there are any unread messages
const hasUnreadMessages = (threads: any): boolean => {
  return (
    threads?.collection?.some((thread: IThread) => {
      // Check if thread status is not 'read' (meaning it's new/unread)
      if (thread.status !== 'read') {
        return true;
      }

      // Also check if the last message hasn't been read by the current user
      if (thread.last_message?.data && !thread.last_message.data.i_read) {
        return true;
      }

      return false;
    }) || false
  );
};

// Action to fetch and update unread messages count - only when there are actually unread messages
const fetchAndUpdateUnreadCount = (): any => {
  return async (dispatch: AppDispatch, getState: RootState) => {
    try {
      // Check if there are any conversations with new message status
      const { threads } = getState().mail;

      // Only fetch unread count if there are new messages
      if (hasUnreadMessages(threads)) {
        const unreadCount = await sdk.getUnreadCount();

        dispatch({
          type: 'UPDATE_UNREAD_MESSAGES_COUNT',
          payload: {
            count: unreadCount,
          },
        });
      }
    } catch (error) {
      console.error('Error fetching unread messages count:', error);
    }
  };
};

// Conditional fetch that only calls the API when needed
const fetchAndUpdateUnreadCountIfNeeded = (): any => {
  return (dispatch: AppDispatch, getState: RootState) => {
    const { threads } = getState().mail;

    // Only dispatch the actual fetch if there are unread messages
    if (hasUnreadMessages(threads)) {
      dispatch(fetchAndUpdateUnreadCount());
    }
  };
};

const triggerThreadsFetching = (
  page: any,
  navigate: any,
  location: Location,
  axiosCancelRequestTokenSource: CancelTokenSource | null
): any => {
  return (dispatch: AppDispatch, getState: RootState) => {
    const threads = getState().mail.threads;

    dispatch(setFetching(threads?.collection?.length === 0, page > 1));

    return sdk
      .getMessagingConversationList(getState().mail.order, page, axiosCancelRequestTokenSource)
      .then(response => {
        const { data } = response;

        const threadCollection = data.data;

        // map the threads and off the fetching threads state
        dispatch(setFetching(false, false));

        // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
        dispatch(storeFetchedThreads(page, threadCollection, data)).then(() => {
          const threads = getState().mail.threads;

          updateMessageThreads(threads);

          if (
            THREADS_ORDER.map(item => item.pathname).includes(location.pathname) &&
            threads !== undefined &&
            threads['collection'].length > 0 &&
            (!getBrowserDetails().isMobile || (getBrowserDetails().isMobile && tabletWidth))
          ) {
            const messageId =
              threads['collection'][0]['uid'] ?? threads['collection'][0]['other_profile']['data']['hash_id'];

            navigate(`${location.pathname}/${messageId}`, {
              replace: true,
            });
          }
        });
      })
      .catch(error => {
        dispatch(setFetchingThreads(false));
        const errorMessage = error?.message || error?.errorMessage || error;

        if ('Raven' in window) {
          Sentry.captureException(errorMessage, { tags: { component: 'Mail', action: 'Threads' } });
        }
        console.error('error thread triggerThreadsFetching: ', error);
      });
  };
};

const fetchThreadTimeout = (navigate: NavigateFunction, location: Location, page = 1): any => {
  return (dispatch: AppDispatch) => {
    dispatch(triggerThreadsFetching(page, navigate, location, null));
  };
};

const fetchReceiverThread = (page = 1): any => {
  return (dispatch: AppDispatch, getState: RootState) => {
    return sdk
      .getMessagingConversationList(getState().mail.order, page, null)
      .then(response => {
        const { data } = response;
        const threadCollection = data.data;

        dispatch(updateThreads(threadCollection));
      })
      .catch(error => {
        if ('Raven' in window) {
          Sentry.captureException(error, { tags: { component: 'Mail', action: 'Threads' } });
        }
        console.error('error thread fetchReceiverThread: ', error);
      });
  };
};

const setConversationPage = (section: any, hashId: any): any => {
  return (dispatch: AppDispatch, getState: RootState) => {
    const conversationState = getState().mail.conversation;
    const shouldKeepConversationState = conversationState && conversationState.uid === hashId;

    dispatch({
      type: 'SET_PAGE',
      payload: {
        order: section,
        conversation: shouldKeepConversationState ? conversationState : {},
      },
    });
  };
};

const enableConversation = (): any => {
  return (dispatch: AppDispatch, getState: RootState) => {
    dispatch({
      type: 'STORE_CONVERSATION',
      payload: {
        ...getState().mail.conversation,
        locked: false,
      },
    });
  };
};

const updateThreads = (collection: any): any => {
  return (dispatch: AppDispatch, getState: RootState) => {
    dispatch({
      type: 'STORE_THREADS',
      payload: {
        ...getState().mail.threads,
        collection,
      },
    });

    // Only fetch unread count if needed when threads are updated
    dispatch(fetchAndUpdateUnreadCountIfNeeded());
  };
};

const updateThreadsCollection = (senderHashId: any): any => {
  return (dispatch: AppDispatch, getState: RootState) => {
    const threads = getState().mail.threads;

    dispatch({
      type: 'STORE_THREADS',
      payload: {
        ...threads,
        collection: threads.collection.map((item: { hash_id: string; to_account_hash_id: string; locked: boolean }) => {
          if (item.hash_id === senderHashId || item.to_account_hash_id === senderHashId) {
            item.locked = false;
          }

          return item;
        }),
      },
    });
    return Promise.resolve();
  };
};

const handleResendMessage = (id: any, navigate: NavigateFunction, location: Location, params: any): any => {
  return (dispatch: AppDispatch, getState: RootState) => {
    // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
    dispatch(updateConvoResendMessages(id)).then(() => {
      const failedMsg = getState().mail.failedMsg;

      dispatch(
        triggerPostMessageProcess(failedMsg.message, failedMsg.type, failedMsg.source, null, params, navigate, location)
      );
    });
  };
};

const updateConvoResendMessages = (id: string) => {
  let failedMsg = {};

  return (dispatch: AppDispatch, getState: RootState) => {
    const conversation = getState().mail.conversation;

    dispatch({
      type: 'STORE_CONVERSATION',
      payload: {
        ...conversation,
        messages: {
          ...conversation.messages,
          data: conversation.messages.data.filter((item: { id: string; uid: string | null }) => {
            if (isUUID(id) && item.uid !== id) {
              return item;
            }

            if (item.id !== id) {
              return item;
            }

            failedMsg = item;
          }),
        },
      },
    });
    dispatch({
      type: 'SET_FAILED_MSG',
      payload: failedMsg,
    });
    return Promise.resolve();
  };
};

const presetOrder = (order: any): any => {
  return {
    type: 'PRESET_ORDER',
    payload: order,
  };
};

const updateConvoMessages = (conversationData: any) => {
  return (dispatch: AppDispatch) => {
    const conversation = conversationData && Array.isArray(conversationData) ? conversationData[0] : conversationData;

    dispatch({
      type: 'STORE_CONVERSATION',
      payload: {
        ...conversation,
        messages: {
          ...conversation.messages,
        },
      },
    });
  };
};

const handleUnblurImage = (messageID: any): any => {
  return (dispatch: AppDispatch, getState: RootState) => {
    const conversation = getState().mail.conversation;
    const { data: messageData } = conversation.messages;

    const msgIdx = messageData.findIndex((msg: { uid: string }) => msg.uid === messageID);

    messageData[msgIdx] = {
      ...messageData[msgIdx],
      isBlurred: false,
    };

    dispatch({
      type: 'UPDATE_CONVERSATION',
      payload: {
        ...conversation,
        messages: {
          ...conversation.messages,
          data: messageData,
        },
      },
    });
  };
};

const updateMessages = (messages: any): any => {
  return (dispatch: AppDispatch, getState: RootState) => {
    const conversation = getState().mail.conversation;
    const conversationMessages = conversation?.messages?.data || [];
    const newMsgData = [...conversationMessages, ...(messages?.data || [])];
    const uniqMessages = {
      data: _uniqBy(_sortBy(newMsgData, 'updated_at.date'), 'updated_at.date'),
      meta: messages.meta,
    };

    dispatch({
      type: 'STORE_CONVERSATION',
      payload: {
        ...conversation,
        messages: {
          ...uniqMessages,
        },
      },
    });
  };
};

const updatePostConvoMessagesPokes = (data: any) => {
  return (dispatch: AppDispatch, getState: RootState) => {
    const conversation = getState().mail.conversation;
    const { can_send_poke, max_pokes, pokes_count } = data.meta;
    const updatedPokesData = (conversation.pokes?.data || []).concat(data.data || []);

    dispatch({
      type: 'STORE_CONVERSATION',
      payload: {
        ...conversation,
        pokes: {
          ...conversation.pokes,
          data: updatedPokesData,
          can_send_poke,
          max_pokes,
          pokes_count,
        },
      },
    });
  };
};

const setSendPoke = (hashId: string): any => {
  return (dispatch: AppDispatch) => {
    sdk
      .sendPoke(hashId)
      .then(response => {
        dispatch(updatePostConvoMessagesPokes(response.data));
      })
      .catch(error => {
        toaster.error('Sorry, something went wrong.');

        if ('Raven' in window) {
          Sentry.captureException(error, { tags: { component: 'Mail', action: 'Poke' } });
        }

        console.error(error);
      });
  };
};

const triggerConvoFetching = (currentConvo: any, params: any, navigate: any, location: any, page = 1): any => {
  const currentHashId = window.location.pathname.split('/')[3] || window.location.pathname.split('/')[2];
  const { hashId } = params;

  return (dispatch: AppDispatch) => {
    const currentId = currentHashId ? currentHashId : hashId;

    if (currentId === '' || currentId === null || currentId === undefined) {
      return false;
    }

    dispatch(setFetchingConversation(_isEmpty(currentConvo)));

    // Add try-catch to handle potential 404 errors
    try {
      dispatch(fetchConversation(currentId, page, params, navigate, location));
    } catch (error) {
      console.error('Error fetching conversation:', error);
      // Handle 404 gracefully by navigating to inbox
      if (error?.response?.status === 404) {
        navigate('/mail/', { replace: true });
      }
    }
  };
};

export const updateConvoDataAfterFetchPage1 = (data: any) => {
  const { can_send_poke, max_pokes, pokes_count, can_request_refund, locked_request_refund } = data.meta;

  const conversation = Array.isArray(data.data) ? data.data[0] : data.data;

  return (dispatch: AppDispatch) => {
    dispatch({
      type: 'STORE_CONVERSATION',
      payload: {
        ...conversation,
        can_request_refund,
        locked_request_refund,
        pokes: {
          ...conversation.pokes,
          can_send_poke,
          max_pokes,
          pokes_count,
        },
        isVideoButtonEnabled: data.meta.video_call_enabled,
      },
    });

    // Only fetch unread count if needed when conversation is stored
    dispatch(fetchAndUpdateUnreadCountIfNeeded());

    return Promise.resolve();
  };
};

const updateConvoMessagesPokes = (data: any) => {
  return (dispatch: AppDispatch, getState: RootState) => {
    const { can_send_poke, max_pokes, pokes_count, can_request_refund } = data.meta;
    const conversation = getState().mail.conversation;
    const conversationMessageData = [...data.data.messages.data, ...conversation.messages.data];
    const conversationPayload = {
      ...data.data,
      can_request_refund,
      pokes: {
        ...conversation.pokes,
        can_send_poke,
        max_pokes,
        pokes_count,
      },
      messages: {
        data: conversationMessageData,
        meta: data.data.messages.meta,
      },
      isVideoButtonEnabled: data.meta.video_call_enabled,
    };

    updateMessageConversations('add', conversationPayload);
    dispatch({
      type: 'STORE_CONVERSATION',
      payload: conversationPayload,
    });
  };
};

const updateMessageandConvo = (): any => {
  return (dispatch: AppDispatch, getState: RootState) => {
    const conversation = getState().mail.conversation;

    dispatch({
      type: 'STORE_CONVERSATION',
      payload: {
        ...conversation,
        messages: {
          ...conversation.messages,
          data: conversation.messages
            ? conversation.messages.data.map((item: any) => {
                item.they_read = true;
                return item;
              })
            : [],
        },
      },
    });
  };
};

const setThread = (newThread: any): any => {
  return (dispatch: AppDispatch) => {
    dispatch({
      type: 'STORE_THREADS',
      payload: newThread,
    });
    return Promise.resolve();
  };
};

const archiveConversation = (archive: any) => {
  return (dispatch: AppDispatch, getState: RootState) => {
    const conversation = getState().mail.conversation;

    dispatch({
      type: 'STORE_CONVERSATION',
      payload: {
        ...conversation,
        archive,
      },
    });
  };
};

const setToggleArchive = (
  hashId: string,
  page: any,
  navigate: NavigateFunction,
  location: Location,
  isArchive = true
): any => {
  return (dispatch: AppDispatch, getState: RootState) => {
    sdk
      .toggleArchive(true, hashId, isArchive)
      .then(response => {
        const archived = response.data.archived;

        dispatch(archiveConversation(archived));

        // Fetch latest unread count after archiving
        dispatch(fetchAndUpdateUnreadCount());

        if (!archived) {
          return dispatch(triggerThreadsFetching(page, navigate, location, null));
        }

        // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
        dispatch(updateThreadsForArchive(response)).then(() => {
          updateMessageThreads(getState().mail.threads);
        });
      })
      .catch(error => {
        toaster.error('Sorry, something went wrong.');

        if ('Raven' in window) {
          Sentry.captureException(error, { tags: { component: 'Mail', action: 'Archive' } });
        }

        console.error(error);
      });
  };
};

const updateThreadsForArchive = (data: any) => {
  return (dispatch: AppDispatch, getState: RootState) => {
    const threads = getState().mail.threads;

    dispatch({
      type: 'STORE_THREADS',
      payload: {
        ...threads,
        collection: threads.collection.filter((thread: IThread) => {
          if (typeof thread.id === 'undefined') {
            return thread.uid !== data.data.uid;
          }

          return thread.id !== data.data.id;
        }),
      },
    });
    return Promise.resolve();
  };
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const closeConversation = (navigate: any, location: any, params: any): any => {
  return (dispatch: AppDispatch) => {
    dispatch({
      type: 'CLOSE_CONVERSATION',
    });

    // Fetch latest unread count when conversation is closed
    dispatch(fetchAndUpdateUnreadCount());

    navigate('/mail');
  };
};

const filterThreads = (navigate: any, location: any, params: any): any => {
  return (dispatch: AppDispatch) => {
    dispatch({
      type: 'RESET_THREADS_FILTER',
    });

    // Only fetch unread count if needed when threads filter is reset
    dispatch(fetchAndUpdateUnreadCountIfNeeded());

    navigate(location.pathname.replace(params.hashId, ''));
  };
};

const handleThreadClick = (
  threadId: any,
  hashId: string,
  params: any,
  navigate: any,
  threadUid: string | null | undefined
): any => {
  return (dispatch: AppDispatch, getState: RootState) => {
    const { order, threads } = getState().mail;

    if (params.hashId === hashId) {
      return false;
    }

    if (threadUid && params.hashId === threadUid) {
      return false;
    }

    navigate(`/mail${order !== 'recent' ? `/${order}` : ''}/${threadUid ?? hashId}`);

    // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
    dispatch(setNewThread(threadUid)).then(() => {
      updateMessageThreads(threads);
    });
  };
};

const setNewThread = (threadUid: string) => {
  return (dispatch: AppDispatch, getState: RootState) => {
    const threads = getState().mail.threads;
    let hasStatusChanged = false;

    const updatedCollection = threads.collection.map((thread: IThread) => {
      if (typeof thread.id === 'undefined' && thread.uid === threadUid) {
        // Check if status is actually changing from unread to read
        if (thread.status !== 'read' || (thread.last_message?.data && !thread.last_message.data.i_read)) {
          hasStatusChanged = true;
        }

        thread.status = 'read';

        if (thread.locked === false && thread.last_message) {
          thread.last_message.data.i_read = true;
        }

        return thread;
      }

      if (thread.id === threadUid) {
        // Check if status is actually changing from unread to read
        if (thread.status !== 'read' || (thread.last_message?.data && !thread.last_message.data.i_read)) {
          hasStatusChanged = true;
        }

        thread.status = 'read';

        if (thread.locked === false && thread.last_message) {
          thread.last_message.data.i_read = true;
        }
      }

      return thread;
    });

    dispatch({
      type: 'SET_NEW_THREADS',
      payload: {
        ...threads,
        collection: updatedCollection,
      },
    });

    // Only fetch latest unread count if thread status actually changed from unread to read
    if (hasStatusChanged) {
      dispatch(fetchAndUpdateUnreadCount());
    }

    return Promise.resolve();
  };
};

const toggleAcctTyping = (): any => {
  return {
    type: 'TOGGLE_OTHER_ACCT_TYPING',
  };
};

const setFetching = (isFecthingThreads: boolean, isFetchingMoreThreads: boolean) => {
  return {
    type: 'START_FETCHING_THREADS',
    payload: {
      fetchingThreads: isFecthingThreads,
      fetchingMoreThreads: isFetchingMoreThreads,
    },
  };
};

const storeFetchedThreads = (page: number, threadCollection: any, data: any) => {
  return (dispatch: AppDispatch, getState: RootState) => {
    const threads = getState().mail.threads;

    dispatch({
      type: 'STORE_FETCHED_THREADS',
      payload: {
        collection:
          page > 1 ? [...(threads.collection ? threads.collection : []), ...threadCollection] : threadCollection,
        meta: {
          pagination: data?.meta?.pagination,
        },
      },
    });

    // Only fetch unread count if needed when threads are fetched
    dispatch(fetchAndUpdateUnreadCountIfNeeded());

    return Promise.resolve();
  };
};

const setFetchingThreads = (fetchingThreads: any) => {
  return {
    type: 'SET_FETCHING_THREADS',
    payload: fetchingThreads,
  };
};

const setFetchingConversation = (fetchingConveration: any) => {
  return {
    type: 'SET_FETCHING_CONVERSATION',
    payload: fetchingConveration,
  };
};

const setMailError = (code: string | number, message: string): any => {
  return {
    type: 'STORE_MAIL_ERROR',
    payload: {
      code: code,
      message: message,
    },
  };
};

const clearMailError = (): any => {
  return {
    type: 'STORE_MAIL_ERROR',
  };
};

const triggerPostMessageProcess = (
  message: any,
  type = '',
  source = null,
  extension: any,
  params: any,
  navigate: any,
  location: any,
  messageUid: string | number | undefined = null
): any => {
  return (dispatch: AppDispatch, getState: RootState) => {
    const { conversation } = getState().mail;
    const hashId = getState().profile.hash_id;
    const { data: messageData } = conversation?.messages || { data: [] };

    const lastMessage = messageData[messageData.length - 1];

    if (message && message.trim() === '') {
      return false;
    }

    const tempMsgId = lastMessage?.id ? lastMessage.id + 1 : Math.floor(Math.random() * 1000);
    const newMessage = {
      id: tempMsgId,
      uid: messageUid,
      hash_id: hashId,
      message,
      updated_at: { date: tsCustomFormat(tsNow(), 'YYYY-MM-DD HH:mm:ss') },
      i_read: true,
      type,
      source,
      isBlurred: false,
    };

    const messagePendingUpdate = {
      message,
      type,
      source,
      tempMsgId: newMessage.uid ?? newMessage.id,
      extension,
    };

    dispatch(storePostMessageData(newMessage, messagePendingUpdate)).then(() => {
      // will update this later after we update handleReplaceMessageId method in action
      if (type !== 'photo') {
        dispatch(triggerReplaceMessageId()).then(() => {
          dispatch(fetchThreadTimeout(navigate, location));
        });
      } else {
        dispatch(fetchThreadTimeout(navigate, location));
      }

      const conversationBody = document.getElementById('conversation_body');

      if (conversationBody) {
        conversationBody.scrollTop = conversationBody.scrollHeight;
      }
    });
  };
};

const storePostMessageData = (newMessage: any, messagePendingUpdate: any): any => {
  return (dispatch: AppDispatch, getState: RootState) => {
    const { threads, conversation } = getState().mail;

    const updatedThreads = {
      ...threads,
      collection: threads.collection.map((item: any) => {
        if (item.uid === conversation?.uid) {
          return {
            ...item,
            last_message: { data: newMessage },
          };
        }
        return item;
      }),
    };

    const existingMessages = conversation?.messages?.data ?? [];
    const newMessageData = {
      ...(conversation?.messages ?? {}),
      data: existingMessages.concat(newMessage),
    };

    dispatch({
      type: 'STORE_POST_MESSAGE',
      payload: {
        threads: updatedThreads,
        conversation: {
          ...conversation,
          messages: newMessageData,
        },
        messagePendingUpdate: {
          ...messagePendingUpdate,
        },
      },
    });
    return Promise.resolve();
  };
};

const handlePhotoMsgActionErr = (error: any): any => {
  return (dispatch: AppDispatch, getState: RootState) => {
    const conversation = getState().mail.conversation;

    toaster.error('Sorry, something went wrong.');

    if (conversation.messages.data.length > 0) {
      conversation.messages.data[conversation.messages.data.length - 1].error = true;
    }

    dispatch(updateConvoMessages(conversation));

    if ('Raven' in window) {
      Sentry.captureException(error, { tags: { component: 'Mail', action: 'Message' } });
    }

    console.error(error);
  };
};

const setDeletePhotoMessage = (option: any, messageID: any, threadUid?: string | number | undefined): any => {
  console.log();
  return (dispatch: AppDispatch) => {
    sdk.deletePhotoMessage(option, messageID, threadUid).catch(error => {
      dispatch(handlePhotoMsgActionErr(error));
    });
  };
};

const handleDeleteImage = (
  newMessage: any,
  messageID: any,
  option: any,
  navigate: NavigateFunction,
  location: Location,
  threadUid?: string | number | undefined
): any => {
  return (dispatch: AppDispatch) => {
    dispatch(updateNewThreadMessages(newMessage, messageID, navigate, location));
    dispatch(setDeletePhotoMessage(option, messageID, threadUid));
  };
};

const updateNewThreadMessages = (newMessage: any, messageID: any, navigate: NavigateFunction, location: Location) => {
  return (dispatch: AppDispatch, getState: RootState) => {
    const { threads, conversation } = getState().mail;
    const { data: messageData } = conversation.messages;
    const lastMessage = messageData[messageData.length - 1];
    const msgIdx = messageData.findIndex((msg: { id: string; uid: string | null }) => {
      if (isUUID(messageID)) {
        return msg.uid === messageID;
      }

      return msg.id === messageID;
    });

    messageData[msgIdx] = {
      ...messageData[msgIdx],
      ...newMessage,
    };

    const newThread =
      messageData[msgIdx].id === lastMessage.id
        ? {
            ...threads,
            collection: threads.collection.map((item: any) => {
              if (item.id === conversation.id) {
                item.last_message = { data: newMessage };
              }

              if (typeof item.id === 'undefined' && conversation.uid === item.uid) {
                item.last_message = { data: newMessage };
              }

              return item;
            }),
          }
        : {
            ...threads,
          };

    // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
    dispatch(updateThreadConvoMessages(newThread, conversation, messageData)).then(() => {
      dispatch(fetchThreadTimeout(navigate, location));
      const conversationBody = document.getElementById('conversation_body');

      if (conversationBody) {
        conversationBody.scrollTop = conversationBody.scrollHeight;
      }
    });
  };
};

const updateThreadConvoMessages = (newThread: any, conversation: any, messageData: any) => {
  return (dispatch: AppDispatch) => {
    dispatch({
      type: 'UPDATE_THREAD_MESSAGES',
      payload: {
        threads: newThread,
        conversation: {
          ...conversation,
          messages: {
            ...conversation.messages,
            data: messageData,
          },
        },
      },
    });
    return Promise.resolve();
  };
};

const handleUnlockOffer = (
  currConvo: any,
  threadId: any,
  hashId: string,
  params: any,
  navigate: any,
  location: any,
  triggerToBuy: boolean,
  callback: () => void
): any => {
  return (dispatch: AppDispatch, getState: RootState) => {
    dispatch(clearMailError());
    return sdk
      .unlockOffer(true, hashId, triggerToBuy)
      .then((): any => {
        dispatch(triggerConvoFetching(currConvo, params, navigate, location));
        dispatch(refreshAccount());
        dispatch(updateUnlockThreadConvo(threadId)).then(() => {
          updateMessageThreads(getState().mail.threads);
          // Fetch latest unread count after unlocking
          dispatch(fetchAndUpdateUnreadCount());
        });
        callback();

        return Promise.resolve();
      })
      .catch((error): any => {
        if (error.status === 404) {
          console.info('Offer is already unlocked');
          return;
        }

        if (getState().mail?.error?.code === 403) {
          return Promise.reject(error);
        }

        toaster.error('Sorry, something went wrong.');
        if ('Raven' in window) {
          Sentry.captureException(error, { tags: { component: 'Mail', action: 'Unlock' } });
        }

        console.error(error);

        return Promise.resolve();
      });
  };
};

const unlockOutsideMessage = (
  hashId: string,
  triggerToBuy: boolean,
  callback: () => void,
  isGenerous: boolean,
  navigate?: NavigateFunction
): any => {
  return (dispatch: AppDispatch, getState: RootState) => {
    sdk
      .unlockOffer(true, hashId, triggerToBuy)
      .then(response => {
        dispatch(refreshAccount());
        callback();

        // Update conversation state to mark as unlocked
        const threadUid = response.data.data.thread_uid ?? hashId;

        dispatch(updateUnlockThreadConvo(threadUid)).then(() => {
          updateMessageThreads(getState().mail.threads);
          // Fetch latest unread count after unlocking
          dispatch(fetchAndUpdateUnreadCount());
        });

        if (navigate) {
          setTimeout(() => (isGenerous ? navigate(`/mail/${threadUid}`) : navigate('/mail/remindMessage=true')), 500);
        }
      })
      .catch(error => {
        if (error.status === 404) {
          console.info('Offer is already unlocked');
          return;
        }

        if (getState().mail?.error?.code === 403) {
          return Promise.reject(error);
        }

        toaster.error('Sorry, something went wrong.');
        if ('Raven' in window) {
          Sentry.captureException(error, { tags: { component: 'Mail', action: 'Unlock' } });
        }

        console.error(error);
      });
  };
};

const updateUnlockThreadConvo = (threadId: any): any => {
  return (dispatch: AppDispatch, getState: RootState) => {
    const { conversation, threads } = getState().mail;

    dispatch({
      type: 'UPDATE_THREAD_MESSAGES',
      payload: {
        threads: {
          ...threads,
          collection: threads.collection.map((thread: IThread) => {
            if (isUUID(threadId) && thread.uid === threadId) {
              thread.locked = false;
              thread.status = 'read';

              if (thread.last_message) {
                thread.last_message.data.i_read = true;
              }

              return thread;
            }

            if (thread.id === threadId) {
              thread.locked = false;
              thread.status = 'read';

              if (thread.last_message) {
                thread.last_message.data.i_read = true;
              }
            }

            return thread;
          }),
        },
        conversation: {
          ...conversation,
          locked: false,
        },
      },
    });
    return Promise.resolve();
  };
};

const handleCancelOffer = (
  hashId: string,
  navigate: NavigateFunction,
  location: any,
  params: any,
  isMobile: boolean
): any => {
  return (dispatch: AppDispatch, getState: RootState) => {
    sdk
      .ignoreOffer(true, hashId)
      .then(() => {
        toaster.success('Offer Removed!');
        dispatch(updateThreadsForCancel()).then(() => {
          const { threads, order } = getState().mail;

          updateMessageThreads(threads);
          // Fetch latest unread count after canceling offer
          dispatch(fetchAndUpdateUnreadCount());

          if (threads && threads.collection.length > 0 && !isMobile) {
            const firstThreadId = threads.collection[0].uid;
            const pathname = THREADS_ORDER.find(item => item.value === order)?.pathname;

            navigate(`${pathname}/${firstThreadId}`, { replace: true });
          }
        });

        dispatch(closeConversation(navigate, location, params));
      })
      .catch(error => {
        toaster.error('Sorry, something went wrong.');
        if ('Raven' in window) {
          Sentry.captureException(error, { tags: { component: 'Mail', action: 'Cancel Offer' } });
        }

        console.error(error);
      });
  };
};

const updateThreadsForCancel = (): any => {
  return (dispatch: AppDispatch, getState: RootState) => {
    const conversation = getState().mail.conversation;
    const threads = getState().mail.threads;

    dispatch({
      type: 'STORE_THREADS',
      payload: {
        ...threads,
        collection: threads.collection.filter((thread: IThread) => {
          return thread.uid !== conversation.uid;
        }),
      },
    });
    return Promise.resolve();
  };
};

const triggerReplaceMessageId = (): any => {
  return async (dispatch: AppDispatch, getState: RootState) => {
    try {
      const { conversation, messagePendingUpdate } = getState().mail;

      if (_isEmpty(conversation) || _isEmpty(messagePendingUpdate)) {
        return;
      }

      const { message, type, source, tempMsgId, extension } = messagePendingUpdate;
      const hashId = conversation.other_profile?.data?.hash_id;
      const { data: messageData } = conversation.messages;

      if (!hashId || conversation.locked) {
        toaster.error('Cannot send message in this conversation.');
        if (conversation?.messages?.data?.length > 0) {
          conversation.messages.data[conversation.messages.data.length - 1].error = true;
        }
        dispatch(updateConvoMessages(conversation));
        return;
      }

      if (message.trim() === '') {
        return false;
      }

      let msgToPost = message;

      if (type === 'photo') {
        const msgResp = JSON.parse(message);

        msgToPost = msgResp.path;
      }

      const postMessageResponse = await sdk.postMessage(hashId, msgToPost, type, extension);

      if (postMessageResponse?.data?.data) {
        const newMessage = {
          ...postMessageResponse.data.data,
          i_read: true,
          message,
          type,
          source,
          isBlurred: false,
        };

        const msgIdx = messageData.findIndex((msg: { id: string; uid: string | null }) => {
          if (isUUID(tempMsgId)) {
            return msg.uid === tempMsgId;
          }

          return msg.id === tempMsgId;
        });

        if (msgIdx !== -1) {
          messageData[msgIdx] = {
            ...messageData[msgIdx],
            ...newMessage,
          };
        }

        dispatch({
          type: 'UPDATE_CONVERSATION_COMPLETE',
          payload: {
            ...conversation,
            messages: {
              ...conversation.messages,
              data: messageData,
            },
          },
        });
      }
    } catch (error: any) {
      const conversation = getState().mail.conversation;

      if (error?.response?.status === 422) {
        toaster.error(error.response?.data?.message || 'Message failed to send.');
      } else if (error?.errorMessage === 'requestBlocked') {
        toaster.error('Slow Down you are going to fast...Please wait!');
      } else {
        toaster.error('Sorry, something went wrong.');
      }

      if (conversation?.messages?.data?.length > 0) {
        conversation.messages.data[conversation.messages.data.length - 1].error = true;
      }

      dispatch(updateConvoMessages(conversation));

      if ('Raven' in window) {
        Sentry.captureException(error, { tags: { component: 'Mail', action: 'Message' } });
      }

      console.error(error);
    }
    return Promise.resolve();
  };
};

const fetchConversation = (hashId: string, page: any, params?: any, navigate?: any, location?: any): any => {
  return (dispatch: AppDispatch, getState: RootState) => {
    if (['unlocked', 'recent', 'locked', 'archive', 'unread'].includes(hashId)) return Promise.resolve();

    return sdk
      .getMessagingConversationByUid(hashId, page)
      .then(response => {
        dispatch(setFetchingConversation(false));

        if (page === 1) {
          const data = response.data;

          // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
          dispatch(updateConvoDataAfterFetchPage1(data)).then(() => {
            updateMessageConversations('add', getState().mail.conversation);
          });

          return;
        }
        dispatch(updateConvoMessagesPokes(response.data));
      })
      .catch(error => {
        if (error?.response?.status === 404) {
          console.warn(`Thread ${hashId} not found - navigating to mail inbox`);
          if (navigate && location) {
            dispatch(closeConversation(navigate, location, params));
          }
          // Don't show error toast for 404s - this is likely just a navigation issue
          return;
        }

        if (error?.errorMessage !== 'requestBlocked') {
          dispatch(closeConversation(navigate, location, params));
          toaster.error('Sorry, something went wrong');
        }

        if ('Raven' in window) {
          Sentry.captureException(error, { tags: { component: 'Mail', action: 'Conversation' } });
        }

        console.error(error);
      });
  };
};

const setblockUser = (hashId: string, username: string, navigate: any): any => {
  return () => {
    sdk
      .blockUser(hashId)
      .then(() => {
        toaster.warn(`You have blocked ${username}`);
        navigate('/blocked');
      })
      .catch(error => {
        toaster.error('Sorry, something went wrong.');
        if ('Raven' in window) {
          Sentry.captureException(error, { tags: { component: 'Mail', action: 'Block' } });
        }
        console.error(error);
      });
  };
};

const handlePostTypingStatus = (hashId: any, threadUid: string | null | undefined): any => {
  return () => {
    sdk.sendTypingIndicator(hashId, threadUid);
  };
};

const handleReportImage = (newMessage: any, messageID: any, navigate: NavigateFunction, location: Location): any => {
  return (dispatch: AppDispatch) => {
    dispatch(updateNewThreadMessages(newMessage, messageID, navigate, location));

    sdk
      .reportPhotoMessage(messageID)
      .then(() => {
        toaster.success('Photo message reported');
        navigate('/blocked');
      })
      .catch(error => {
        dispatch(handlePhotoMsgActionErr(error));
      });
  };
};

export const uploadPhotoMessage = (formData: any, otherHashId: any, isPrivate = false, threadUid?: string): any => {
  return (dispatch: AppDispatch) => {
    // validate 15mb file size
    if (!formData || typeof formData.size === 'undefined' || formData.size >= 15000000) {
      dispatch(endPendingUpload(isPrivate));
      dispatch(
        displayFailMessage({
          info: 'Error uploading photo due to file size. Please try again or ',
          linkInfo: 'contact support.',
          url: `${MasterConfig.FULL_URL}/faq/giving-feedback`,
          newWindow: true,
        })
      );
      return;
    }

    dispatch(pendingUpload(isPrivate));

    return dispatch(processMessageSlsPhotoMessage(formData, otherHashId, threadUid, isPrivate));
  };
};

const doS3Upload = (
  uploadUrl: string,
  newFile: File,
  photoUrl: string,
  path: string,
  messageId: number | string,
  fileSizeDelay: number,
  isPrivate = false
) => {
  return (dispatch: AppDispatch) => {
    sdk.uploadToS3(uploadUrl, newFile).then(
      () => {
        setTimeout(() => {
          sdk
            .getPhoto(photoUrl)
            .then(resp => {
              if (!('data' in resp)) {
                Sentry.captureException('Message Photo Upload failed to upload/process', {
                  extra: {
                    photoPath: path,
                    data: resp,
                  },
                });
                dispatch(stopPendingPhotoUpload(isPrivate));
                return false;
              }
              dispatch(endPendingUpload(isPrivate));
            })
            .catch(error => {
              Sentry.captureException('Message Photo Upload failed to upload/process', {
                extra: {
                  photoPath: photoUrl,
                  data: error,
                },
              });
              dispatch(stopPendingPhotoUpload(isPrivate));
            });
        }, fileSizeDelay); // delay based on file size
      },
      () => {
        dispatch(deletePhoto(messageId));
        dispatch(stopPendingPhotoUpload(isPrivate));
      }
    );
  };
};

export const processMessageSlsPhotoMessage = (
  formData: any,
  otherHashId: string,
  threadUid: string,
  isPrivate = false
): any => {
  return (dispatch: AppDispatch, getState: RootState) => {
    dispatch(pendingUpload(isPrivate));

    const useDirectUpload = getState().profile.direct_s3_upload;
    const fileExtension = formData.type.substr(formData.type.lastIndexOf('/') + 1);
    const fileSizeDelay = getFileSizeDelay(formData.size);

    return sdk
      .preUploadPhotoMessage(threadUid, fileExtension)
      .then(response => {
        const apiResponseData = response.data;
        const uploadUrl = apiResponseData?.response?.upload;
        const photoPath = apiResponseData?.response?.uuid;
        const photoUrl = apiResponseData?.response?.cloudfront_url;

        if (useDirectUpload === true) {
          const fileName = photoPath + '.' + fileExtension;
          let newFile = null;

          try {
            newFile = new File([formData], fileName, { type: formData.type });
          } catch (err) {
            console.warn(err);
            newFile = new File([formData], fileName, { type: formData.type, lastModified: Date.now() });
          }

          dispatch(doS3Upload(uploadUrl, newFile, photoUrl, photoPath, photoPath, fileSizeDelay, isPrivate));
        } else {
          dispatch(endImageUploadLoader(response, isPrivate));
        }

        const date = moment().format('YY–MM-DD HH:mm:ss');
        const photoMessageData = {
          updated_at: {
            date,
          },
          uid: photoPath,
          hash_id: otherHashId,
          type: 'photo',
          is_deleted: false,
          is_visible: true,
          message: JSON.stringify({
            path: photoPath,
            sizes: ['820px'],
          }),
          isBlurred: false,
          isUploading: true,
          urls: {
            '820px': photoUrl,
          },
          threadUid,
        };

        return photoMessageData;
      })
      .catch(error => {
        console.error('Error uploading photo message', error);
        uploadErrorFlow(error, isPrivate, dispatch);
      });
  };
};

export {
  triggerThreadsFetching,
  fetchThreadTimeout,
  setConversationPage,
  enableConversation,
  updateThreadsCollection,
  setThread,
  updateMessages,
  toggleAcctTyping,
  updateMessageandConvo,
  triggerConvoFetching,
  triggerPostMessageProcess,
  triggerReplaceMessageId,
  handleDeleteImage,
  handleUnblurImage,
  setSendPoke,
  handleReportImage,
  handleResendMessage,
  handleUnlockOffer,
  setToggleArchive,
  handleCancelOffer,
  closeConversation,
  handleThreadClick,
  setblockUser,
  handlePostTypingStatus,
  setDeletePhotoMessage,
  presetOrder,
  updateThreads,
  unlockOutsideMessage,
  fetchReceiverThread,
  setMailError,
  filterThreads,
  fetchAndUpdateUnreadCount,
  fetchAndUpdateUnreadCountIfNeeded,
};
